import React from 'react'
import ExpandableCard from '../ExpandableCard'
import LinkRenderer from './LinkRenderer'
import SmartMarkdown from './SmartMarkdown'
import TrimesterGraphics from './TrimesterGraphics'
import { humanizedWeeksAndDays } from './helpers'
import states from './states.json'
import './AbortionDetails_styles.scss'
import visit_icon from './icon_pill_visit_blue.svg'
import pickup_icon from './icon_pill_pickup_blue.svg'
import envelope_icon from './icon_envelope_blue.svg'
import { NavigateClinicOffering } from '../../../shared/constants.js'

const TelehealthAbortionDetails = ({ clinic, lexicon, locale }) => {
  const clinicType = 'hybrid'

  let fullAddress = ''
  if (clinic.address_1) {
    fullAddress += `${clinic.address_1}, `
  }
  if (clinic.address_2) {
    fullAddress += `${clinic.address_2}, `
  }
  fullAddress += `${clinic.city}, ${clinic.state} ${clinic.zip}`

  let isPillVisitAvailable
  let isPillDeliveryAvailable
  let isPillPickupAvailable

  if (
    clinic.telehealth_services &&
    clinic.telehealth_services[clinic.state] &&
    clinic.telehealth_services[clinic.state].offerings
  ) {
    isPillVisitAvailable = clinic.telehealth_services[
      clinic.state
    ].offerings.includes(NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_VISIT)
    isPillDeliveryAvailable = clinic.telehealth_services[
      clinic.state
    ].offerings.includes(
      NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_DELIVERY
    )
    isPillPickupAvailable = clinic.telehealth_services[
      clinic.state
    ].offerings.includes(NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_PICKUP)
  } else {
    isPillVisitAvailable =
      clinic.known_offerings &&
      !!clinic.known_offerings[
        NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_VISIT
      ]
    isPillDeliveryAvailable =
      clinic.known_offerings &&
      !!clinic.known_offerings[
        NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_DELIVERY
      ]
    isPillPickupAvailable =
      clinic.known_offerings &&
      !!clinic.known_offerings[
        NavigateClinicOffering.ABORTION_TELEHEALTH_PILL_PICKUP
      ]
  }

  if (
    !isPillVisitAvailable &&
    !isPillDeliveryAvailable &&
    !isPillPickupAvailable
  )
    return null

  const timeLexicon = lexicon.subset('time')

  const isTelemedFetalTone =
    clinic.abortion_telehealth_gal_mode === 'FETAL_TONE'

  const clinicPillVisitMaxGalInState = clinic.abortion_telehealth_gal_days

  const isPillVisitMaxGalAvaiable = !!clinicPillVisitMaxGalInState
  const numberOfStatesServedByTelehealth =
    clinic.telehealth_service_states.length

  let telehealthSnippet = ''
  if (clinic.pill_gal_mode === 'FETAL_TONE')
    telehealthSnippet = `${timeLexicon.get('through')} ${lexicon.get(
      'around_6_weeks'
    )}`
  else if (clinic.abortion_pill_gestational_age_max)
    telehealthSnippet = `${timeLexicon.get('through')} ${humanizedWeeksAndDays(
      clinic.abortion_telehealth_gal_days,
      timeLexicon
    )}`
  else telehealthSnippet = lexicon.get('call_snippet')
  /* eslint-enable no-undef -- selectedStateAbbrev */

  // computing pill visit copies
  let pillVisitText
  let bullet_1pv = ''
  let bullet_2pv = ''

  if (clinicType === 'hybrid') {
    if (isTelemedFetalTone)
      bullet_1pv = lexicon.get('pill_visit.lmp_bullet', {
        humanized_time: lexicon.get('around_6_weeks'),
      })
    else if (clinicPillVisitMaxGalInState)
      bullet_1pv = lexicon.get('pill_visit.lmp_bullet', {
        humanized_time: humanizedWeeksAndDays(
          clinicPillVisitMaxGalInState,
          timeLexicon
        ),
      })

    if (isPillPickupAvailable && isPillDeliveryAvailable) {
      bullet_2pv = `
      ${lexicon.get('pill_visit.requirement_bullet')}
      ${lexicon.get('pill_visit.pill_pickup')}
      ${lexicon.get('pill_visit.and_or')}
      ${lexicon.get('pill_visit.pill_delivery')}
      `
    } else if (isPillPickupAvailable) {
      bullet_2pv = `
      ${lexicon.get('pill_visit.requirement_bullet')}
      ${lexicon.get('pill_visit.pill_pickup')}
      `
    } else if (isPillDeliveryAvailable) {
      bullet_2pv = `
      ${lexicon.get('pill_visit.requirement_bullet')}
      ${lexicon.get('pill_visit.pill_delivery')}
      `
    }

    const isShowingBookingForPillVisit =
      clinic.abortion_booking_url &&
      (clinic.abortion_booking_type === 'virtual_only' ||
        clinic.abortion_booking_type === 'both')

    const book_now_pv = isShowingBookingForPillVisit
      ? lexicon.get('pill_visit.book', { url: clinic.abortion_booking_url })
      : ''

    const pillVisitBullets = [bullet_1pv, bullet_2pv]
      .filter((str) => !!str)
      .map((str) => `+ ${str}`)
      .join('\n ')

    pillVisitText = `${lexicon.get(
      'pill_visit.eligible'
    )}\n\n ${pillVisitBullets}\n\n ${book_now_pv}`
  } else {
    let bullet_1m = ''
    if (
      clinic['telehealth_services'][clinic.state]['telemed_gal_mode'] ===
      'FETAL_TONE'
    )
      bullet_1m = lexicon.get('medical.known_both', {
        humanized_time: lexicon.get('around_6_weeks'),
      })
    else if (clinicPillVisitMaxGalInState) {
      bullet_1m = lexicon.get('medical.known_both', {
        humanized_time: humanizedWeeksAndDays(
          clinicPillVisitMaxGalInState,
          timeLexicon
        ),
      })
    } else bullet_1m = lexicon.get('medical.known_one')

    pillVisitText = lexicon.get('medical.description', { bullet_1m })
  }

  // computing pill pickup copies

  let pillPickupSnippet
  if (isPillPickupAvailable && clinicType === 'hybrid')
    pillPickupSnippet = lexicon.get('pill_pickup.snippet')
  else if (isPillPickupAvailable)
    pillPickupSnippet = lexicon.get('pill_pickup.snippet_2')

  let full_address = ''
  if (isPillPickupAvailable) {
    full_address = fullAddress

    if (clinic.address_private) {
      if (locale === 'es' && clinic.address_private_text_es)
        full_address = clinic.address_private_text_es
      else if (locale === 'en' && clinic.address_private_text)
        full_address = clinic.address_private_text
      else full_address = lexicon.get('pill_pickup.default_private')
    }
  }

  let pillPickupBullets
  let pillPickupText

  if (isPillPickupAvailable && clinicType === 'hybrid') {
    pillPickupBullets = lexicon.get('pill_pickup.bullets', {
      full_address,
      map_link: `http://maps.google.com?daddr=${fullAddress
        .split(' ')
        .join('+')}`,
      state: states[clinic.state],
    })

    pillPickupText = `**${lexicon.get(
      'pill_pickup.instruction_with_delivery'
    )} ${lexicon.get('pill_pickup.at_location', {
      location: '',
    })}**\n${pillPickupBullets}`
  } else if (isPillPickupAvailable) {
    const bullet1 = isPillDeliveryAvailable
      ? lexicon.get('pill_pickup.instruction_with_delivery')
      : lexicon.get('pill_pickup.instruction_without_delivery')

    const bullet2 = clinic.telehealth_services[clinic.state][
      `website_pill_pickup_note_${locale}`
    ]
      ? lexicon.get('pill_pickup.custom_location', {
          note: clinic.telehealth_services[clinic.state][
            `website_pill_pickup_note_${locale}`
          ],
        })
      : lexicon.get('pill_pickup.default_location')

    pillPickupText = [bullet1, bullet2]
      .map((bullet) => `+ ${bullet}\n`)
      .join('')
  }

  // computing pill delivery copies
  let pillDeliverySnippet

  if (clinicType === 'hybrid') {
    pillDeliverySnippet = lexicon.get('pill_delivery.snippet', {
      state: states[clinic.state],
    })

    if (numberOfStatesServedByTelehealth === 2) {
      pillDeliverySnippet += ` ${lexicon.get('pill_delivery.or')} ${
        states[
          Object.keys(clinic.telehealth_services).filter(
            (key) => key !== clinic.state
          )
        ]
      } ` // Requires a mailing address in New Mexico or Texas
    } else if (numberOfStatesServedByTelehealth > 2) {
      pillDeliverySnippet += ` ${lexicon.get('pill_delivery.other_states', {
        number: numberOfStatesServedByTelehealth - 1,
      })}` // Requires a mailing address in New Mexico or 5 other eligible U.S. states
    }
  } else {
    pillDeliverySnippet =
      clinic[`telehealth_shipping_estimate_${locale}`] ||
      lexicon.get('pill_delivery.default_shipping')
  }

  const otherStatesText = clinic.telehealth_service_states
    .filter((state_abbrev) => state_abbrev !== clinic.state) // exclude current state
    .map((state_abbrev) => states[state_abbrev]) // convert state abbrevs to state name
    .map((state_name, i, arr) => {
      // add "," and "or" as needed
      if (i === arr.length - 1) {
        if (arr.length === 1)
          return `${lexicon.get('pill_delivery.or')} ${state_name}`
        return `, ${lexicon.get('pill_delivery.or')} ${state_name}`
      }
      return `, ${state_name}`
    })
    .join('')

  let pillDeliveryText

  if (clinicType === 'hybrid') {
    pillDeliveryText = `${lexicon.get(
      'pill_delivery.description'
    )}\n+ **${lexicon.get('pill_delivery.must_be', {
      state: states[clinic.state],
    })}**`

    if (numberOfStatesServedByTelehealth > 1)
      pillDeliveryText += otherStatesText
    if (numberOfStatesServedByTelehealth > 2) pillDeliveryText += 's'
  } else
    pillDeliveryText = `${lexicon.get(
      'pill_delivery.description'
    )}\n+ ${lexicon.get('pill_delivery.must_be', {
      state: states[clinic.state],
    })}`

  let pillDeliveryBullets = lexicon.get('pill_delivery.bullets')
  if (
    clinic.known_offerings &&
    clinic.known_offerings[NavigateClinicOffering.ABORTION_MAILING_PO_BOX]
  )
    pillDeliveryBullets += `\n+ ${lexicon.get('pill_delivery.po_box_allowed')}`
  pillDeliveryText += `\n${pillDeliveryBullets}`

  return (
    <div id="TelehealthAbortionDetails" className="abortion-section">
      <h3>{lexicon.get('telehealth_title')}</h3>
      {isPillVisitAvailable && (
        <ExpandableCard
          icon={visit_icon}
          title={lexicon.get('pill_visit.title')}
          snippet={telehealthSnippet}
        >
          {isPillVisitMaxGalAvaiable ? (
            <TrimesterGraphics
              ageCap={clinicPillVisitMaxGalInState}
              lexicon={lexicon.subset('TrimesterGraphics')}
              galMode={clinic.abortion_telehealth_gal_mode}
            />
          ) : (
            <SmartMarkdown>
              {lexicon.get('pill_visit.unknown_lmp')}
            </SmartMarkdown>
          )}
          <SmartMarkdown components={{ a: LinkRenderer }}>
            {pillVisitText}
          </SmartMarkdown>
        </ExpandableCard>
      )}

      {isPillPickupAvailable && (
        <ExpandableCard
          icon={pickup_icon}
          title={lexicon.get('pill_pickup.title')}
          snippet={pillPickupSnippet}
        >
          <SmartMarkdown>{pillPickupText}</SmartMarkdown>
        </ExpandableCard>
      )}

      {isPillDeliveryAvailable && (
        <ExpandableCard
          icon={envelope_icon}
          title={lexicon.get('pill_delivery.title')}
          snippet={pillDeliverySnippet}
        >
          <SmartMarkdown>{pillDeliveryText}</SmartMarkdown>
        </ExpandableCard>
      )}
    </div>
  )
}

export default TelehealthAbortionDetails
